import { useNavigate, useOutletContext } from 'react-router-dom';

import { upsertFacilityProvider } from 'api/Admin/Practice/Providers';

import { iaRa, optionify } from 'lib/helpers/utility';
import { useStates } from 'lib/hooks/queries/states/useStates';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { showAlert } from 'components/shared/Alert/Alert';
import { withErrorBoundary } from 'components/shared/Error/Boundary';

import { useFacilityProviderForm } from '../lib/useFacilityProviderForm';

import CreateUpdateFacilityProviderForm from './CreateUpdateFacilityProviderForm';

const CreateUpdateFacilityProvider = ({ isOpen, handleClose, item }) => {
  const { data: statesData } = useStates();
  const states = optionify(statesData?.states);
  const { practice } = useOutletContext();
  const navigate = useNavigate();

  const practiceValues = {
    facility_id: item?.id,
    id: item?.practice_id,
    name: item?.facility_name,
    facility_npi: item?.facility_npi,
    facility_atypical: item?.facility_id,
    practiceAddress: {
      address_ln_1: item?.facility_addr_1,
      address_ln_2: item?.facility_addr_2,
      city: item?.facility_city,
      state: item?.facility_state,
      zip: item?.facility_zip ?? ''
    }
  };

  const onSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    const { facility_npi, facility_id } = values;
    if (!facility_npi && !facility_id) {
      showAlert({
        title: 'Provide either facility NPI or facility atypical ID',
        color: 'danger'
      });
      setSubmitting(false);
      return;
    }
    mutateFacilityProvider.mutate(values);
    formik.resetForm();
    setSubmitting(false);
  };

  const formik = useFacilityProviderForm({
    practice: item ? practiceValues : practice,
    onSubmit,
    validate: isOpen
  });

  const loadedMembers = iaRa(practice?.members).map((member) => {
    return { label: `${member.f_name} ${member.l_name}`, value: member.id };
  });

  const mutateFacilityProvider = useCustomMutation({
    mutationFn: (values) =>
      upsertFacilityProvider(navigate, {
        newFacilityProvider: {
          ...values
        }
      }),
    invalidateQueryKey: ['facility-provider'],
    successMessage: item ? 'Facility provider updated' : 'Facility provider created',
    errorMessage: item ? "Couldn't update facility provider" : "Couldn't create facility provider",
    onSuccess: () => {
      handleClose();
    }
  });

  if (loadedMembers.length === 0) {
    return null;
  }

  return (
    <CreateUpdateFacilityProviderForm
      formik={formik}
      isLoading={mutateFacilityProvider.isLoading}
      open={isOpen}
      type={item !== null ? 'update' : 'create'}
      handleClose={() => handleClose()}
      states={states}
    />
  );
};

export default withErrorBoundary(CreateUpdateFacilityProvider);
